import React from 'react';

import Typography from '@mui/material/Typography';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BREAKPOINTS_VALUES, colorsPalette } from '../../theme';
import { LazyLoadImage } from '../LazyloadImage';
import { CategoryLabel } from '../UI/CategoryLabel';
import { ProgressLine } from '../UI/ProgressLine';
import useStyles from './styles';

interface PathwayInterface {
  videoWidth: number | null;
  background?: string | null;
  handleClick?: (id: string) => void;
  id?: string | null;
  name?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  duration?: string | null;
  countVideos?: number | null;
  slug?: string | null;
  progress?: number;
  wrapperClass?: string;
  withHover?: boolean;
}

const Pathway: React.FC<PathwayInterface> = ({
  videoWidth,
  background,
  name,
  imageUrl,
  description,
  duration,
  countVideos,
  slug,
  progress,
  wrapperClass,
  withHover = true,
}) => {
  const { t } = useTranslation();
  const classes = useStyles(background, videoWidth, withHover, Boolean(slug))();
  const videosCount = countVideos ? `${countVideos} ${countVideos > 1 ? 'videos' : 'video'}` : '';
  const link = `/pathway/${slug}`;

  return (
    <div data-testid="pathwayCard" className={cx(classes.pathwayWrap, wrapperClass)}>
      <div className={classes.wrap}>
        {/*TODO change to <Link /> when pathway page will be made*/}
        {slug ? (
          <Link data-testid="imageWrapperLink" to={link}>
            <div className={classes.imgWrap}>
              {imageUrl && (
                <LazyLoadImage
                  className={classes.videoImg}
                  imageWrapperClass={classes.imageLazyWrapper}
                  path={imageUrl}
                  alt={name}
                  draggable={false}
                  srcset={[{ width: 420 }, { width: 370 }, { width: 350 }, { width: 340 }]}
                  sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 420px, (max-width: ${BREAKPOINTS_VALUES['md']}) 370px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 350px, 340px`}
                  data-testid="pathwayImage"
                />
              )}
            </div>
          </Link>
        ) : (
          <div className={classes.imgWrap}>
            {imageUrl && (
              <LazyLoadImage
                className={classes.videoImg}
                imageWrapperClass={classes.imageLazyWrapper}
                path={imageUrl}
                alt={name}
                draggable={false}
                srcset={[{ width: 420 }, { width: 370 }, { width: 350 }, { width: 340 }]}
                sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 420px, (max-width: ${BREAKPOINTS_VALUES['md']}) 370px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 350px, 340px`}
              />
            )}
          </div>
        )}
        <div className={classes.catDifWrap}>
          <CategoryLabel
            name={t('button.pathway')}
            bgColor={colorsPalette.pink}
            textColor={colorsPalette.blueDark}
          />
        </div>
        <div className={classes.textWrap}>
          {/*TODO change to <Link /> when pathway page will be made*/}
          {slug ? (
            <Link data-testid="titleLink" to={link} className={classes.link}>
              <Typography variant="h3" component="h3" className={classes.title}>
                {name}
              </Typography>

              {description && (
                <Typography className={classes.description}>{description}</Typography>
              )}
            </Link>
          ) : (
            <>
              <Typography variant="h3" component="h3" className={classes.title}>
                {name}
              </Typography>

              {description && (
                <Typography className={classes.description}>{description}</Typography>
              )}
            </>
          )}
          <Typography data-testid="duration" className={classes.duration}>
            {videosCount + ' • ' + duration}
          </Typography>
          {(progress || progress === 0) && <ProgressLine width={progress} />}
        </div>
      </div>
    </div>
  );
};

export default Pathway;
